/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';

const MONTHS  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const QUARTERS = [
  { label: 'Q1', num: 1, months: [1,2,3] },
  { label: 'Q2', num: 2, months: [4,5,6] },
  { label: 'Q3', num: 3, months: [7,8,9] },
  { label: 'Q4', num: 4, months: [10,11,12] },
];

const C = {
  charcoal: '#2F3A42', navy: '#445366', mid: '#7A8A96', border: '#D8DEE3',
  cream: '#F7F6EF', green: '#7EA594', greenLight: '#EAF2EF',
  amber: '#E07B00', amberLight: '#FFF3E0',
  white: '#FFFFFF', red: '#DC3545', redLight: '#FFF0F0',
  skyBlue: '#BCD9E5', skyText: '#3A8FA3', skyLight: '#EAF4F8',
};

const LOG_CATEGORY_LABELS = {
  capex:          'Capex / Asset Purchase',
  board_decision: 'Board Decision',
  legal:          'Legal / Compliance',
  personnel:      'Personnel Change',
  regulatory:     'Regulatory / Permit',
  distribution:   'Distribution / Dividend',
  other:          'Other',
};

const fmt    = (n) => (n !== null && n !== undefined && n !== 0) ? `€${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
const fmtPct = (n, total) => (n && total) ? ((n / total) * 100).toFixed(1) + '%' : '—';

function govDailyRev(dailyRows, monthNum) {
  return (dailyRows || [])
    .filter(d => new Date(d.date).getMonth()+1 === monthNum && (d.total_revenue||0) > 0)
    .reduce((s,d) => s+(d.total_revenue||0), 0) || null;
}

function calcEBITDA(rows, isSassys) {
  const sum = (key) => rows.reduce((a, r) => a + (r?.[key] || 0), 0);
  return sum('actual_revenue')
    - sum('food_cost')
    - (isSassys ? 0 : sum('bev_cost'))
    - (isSassys ? (sum('oedipus_rent') + sum('oedipus_utilities') + sum('oedipus_rev_share')) : 0)
    - sum('kitchen_payroll')
    - (isSassys ? 0 : sum('foh_payroll'))
    - sum('lm_prep_payroll_alloc')
    - sum('lm_transfers_alloc')
    - sum('group_costs')
    - sum('other_opex');
}

function PLRow({ label, cy, ly, bold, indent, highlight, isRevenue, dimIfZero, isMobile }) {
  const vsLY    = (cy != null && ly != null) ? cy - ly : null;
  const vsLYPct = (cy != null && ly != null && ly !== 0) ? ((cy - ly) / Math.abs(ly) * 100) : null;
  const vsColor = vsLYPct == null ? C.mid
    : isRevenue
      ? (vsLYPct >= 0 ? C.green : C.red)
      : (vsLYPct <= 0 ? C.green : C.red);
  const dim = dimIfZero && !cy && !ly;
  if (dim) return null;
  const p  = isMobile ? '6px 4px' : '7px 16px 7px 0';
  const pl = (!isMobile && indent) ? 28 : (isMobile ? 4 : 0);
  const fs = isMobile ? 11 : 13;
  return (
    <tr style={{ background: highlight ? C.amberLight : 'transparent' }}>
      <td style={{ padding: p, borderBottom: `1px solid ${C.border}`, fontSize: fs, fontWeight: bold ? 700 : 400, color: C.charcoal, paddingLeft: pl }}>{label}</td>
      <td style={{ padding: isMobile?'6px 4px':'7px 0 7px 16px', borderBottom: `1px solid ${C.border}`, fontSize: fs, fontWeight: bold ? 800 : 600, color: C.charcoal, textAlign: 'right' }}>{cy != null ? fmt(Math.round(cy)) : '—'}</td>
      <td style={{ padding: isMobile?'6px 4px':'7px 0 7px 16px', borderBottom: `1px solid ${C.border}`, fontSize: isMobile?10:13, color: C.mid, textAlign: 'right' }}>{ly != null ? fmt(Math.round(ly)) : '—'}</td>
      <td style={{ padding: isMobile?'6px 4px':'7px 0 7px 16px', borderBottom: `1px solid ${C.border}`, fontSize: isMobile?10:13, fontWeight: 600, color: vsColor, textAlign: 'right' }}>
        {vsLYPct != null ? `${vsLYPct >= 0 ? '+' : ''}${vsLYPct.toFixed(1)}%` : '—'}
      </td>
    </tr>
  );
}

export default function ShareholderView() {
  const [state, setState]           = useState('loading');
  const [linkRecord, setLinkRecord] = useState(null);
  const [monthly, setMonthly]       = useState([]);
  const [monthlyLY, setMonthlyLY]   = useState([]);
  const [dailyRev, setDailyRev]     = useState([]);
  const [dailyRevLY, setDailyRevLY] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [qRecord, setQRecord]       = useState(null);
  const [readConfirmed, setReadConfirmed] = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');
    if (!token) { setState('invalid'); return; }
    load(token);
  }, []);

  async function load(token) {
    const { data: link, error } = await supabase
      .from('shareholder_links')
      .select('*')
      .eq('token', token)
      .single();

    if (error || !link) { setState('invalid'); return; }

    const { venue, quarter, year } = link;
    const q = QUARTERS.find(q => q.num === quarter);
    if (!q) { setState('invalid'); return; }

    const [{ data: mData }, { data: mDataLY }, { data: dData }, { data: dDataLY }, { data: lData }, { data: qData }] = await Promise.all([
      supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year).in('month', q.months),
      supabase.from('results_monthly').select('*').eq('venue', venue).eq('year', year - 1).in('month', q.months),
      supabase.from('results_daily').select('date,total_revenue').eq('venue', venue).eq('year', year),
      supabase.from('results_daily').select('date,total_revenue').eq('venue', venue).eq('year', year - 1),
      supabase.from('governance_log').select('*').eq('venue', venue).eq('year', year).in('month', q.months).order('month'),
      supabase.from('governance_quarterly').select('*').eq('venue', venue).eq('year', year).eq('quarter', quarter).single(),
    ]);

    if (!qData?.published) { setState('invalid'); return; }

    setLinkRecord(link);
    setMonthly(mData || []);
    setMonthlyLY(mDataLY || []);
    setDailyRev(dData || []);
    setDailyRevLY(dDataLY || []);
    setLogEntries(lData || []);
    setQRecord(qData);
    if (link.read_at) setReadConfirmed(true);
    setState('ok');
  }

  async function handleConfirmRead() {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('token');
    if (!token || readConfirmed) return;
    await supabase
      .from('shareholder_links')
      .update({ read_at: new Date().toISOString() })
      .eq('token', token);
    setReadConfirmed(true);
  }

  if (state === 'loading') return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.mid, fontSize: 14 }}>Loading…</div>
    </div>
  );

  if (state === 'invalid') return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontSize: 32, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Report not available</div>
        <div style={{ fontSize: 13, color: C.mid, lineHeight: 1.6 }}>
          This link is invalid or the report has not been published. Please contact the board.
        </div>
      </div>
    </div>
  );

  const { venue, quarter, year } = linkRecord;
  const q        = QUARTERS.find(q => q.num === quarter);
  const isSassys = venue === 'Sassys';
  const bvName = venue === 'Box Jordaan'  ? 'FOBS B.V.'
               : venue === 'Box Plantage' ? 'Box Social B.V.'
               : venue === 'Sassys'       ? 'Box Social B.V.'
               : 'Lekker Mate B.V.';
  const tradingName = venue === 'Box Jordaan'  ? 'Box Sociaal Jordaan'
                    : venue === 'Box Plantage' ? 'Box Sociaal Plantage'
                    : venue;
  const sumCY = (key) => monthly.reduce((a, r) => a + (r?.[key] || 0), 0);
  const sumLY = (key) => monthlyLY.reduce((a, r) => a + (r?.[key] || 0), 0);

  // CY — mirrors Governance quarterly cy object
  const cy = {
    revenue:         q.months.reduce((s,m) => s + (govDailyRev(dailyRev, m) || (monthly.find(r=>r.month===m)?.actual_revenue||0)), 0),
    food_cost:       sumCY('food_cost'),
    bev_cost:        sumCY('bev_cost'),
    oedipus:         sumCY('oedipus_rent') + sumCY('oedipus_utilities') + sumCY('oedipus_rev_share'),
    kitchen_payroll: sumCY('kitchen_payroll'),
    foh_payroll:     sumCY('foh_payroll'),
    lm_total:        sumCY('lm_prep_payroll_alloc') + sumCY('lm_transfers_alloc'),
    group_costs:     sumCY('group_costs'),
    other_opex:      sumCY('other_opex'),
    net_profit:      sumCY('net_profit'),
    covers:          sumCY('actual_covers'),
  };
  cy.payroll      = cy.kitchen_payroll + (isSassys ? 0 : cy.foh_payroll);
  cy.ebitda       = cy.revenue - cy.food_cost
    - (isSassys ? 0 : cy.bev_cost) - (isSassys ? cy.oedipus : 0)
    - cy.payroll - cy.lm_total - cy.group_costs - cy.other_opex;
  cy.totalExpense = cy.revenue - cy.ebitda;

  // LY — mirrors Governance quarterly ly object
  const ly = {
    revenue:         q.months.reduce((s,m) => s + (govDailyRev(dailyRevLY, m) || (monthlyLY.find(r=>r.month===m)?.actual_revenue||0)), 0),
    food_cost:       sumLY('food_cost'),
    bev_cost:        sumLY('bev_cost'),
    oedipus:         sumLY('oedipus_rent') + sumLY('oedipus_utilities') + sumLY('oedipus_rev_share'),
    kitchen_payroll: sumLY('kitchen_payroll'),
    foh_payroll:     sumLY('foh_payroll'),
    lm_total:        sumLY('lm_prep_payroll_alloc') + sumLY('lm_transfers_alloc'),
    group_costs:     sumLY('group_costs'),
    other_opex:      sumLY('other_opex'),
    net_profit:      sumLY('net_profit'),
    covers:          sumLY('actual_covers'),
  };
  ly.payroll      = ly.kitchen_payroll + (isSassys ? 0 : ly.foh_payroll);
  ly.ebitda       = ly.revenue - ly.food_cost
    - (isSassys ? 0 : ly.bev_cost) - (isSassys ? ly.oedipus : 0)
    - ly.payroll - ly.lm_total - ly.group_costs - ly.other_opex;
  ly.totalExpense = ly.revenue - ly.ebitda;

  // Chart colours — pastel, CY vs LY
  const COL_CY = '#7B9FBF';  // pastel navy — current year
  const COL_LY = '#E8C08A';  // pastel orange — last year

  // Chart data — one entry per quarter month, CY vs LY
  const chartData = q.months.map(m => {
    const r   = monthly.find(row => row.month === m);
    const rLY = monthlyLY.find(row => row.month === m);
    const ebitdaCY = r   ? calcEBITDA([r],   isSassys) : null;
    const ebitdaLY = rLY ? calcEBITDA([rLY], isSassys) : null;
    return {
      month:     MONTHS[m - 1],
      revenue:   govDailyRev(dailyRev, m)   || r?.actual_revenue   || null,
      revenueLY: govDailyRev(dailyRevLY, m) || rLY?.actual_revenue || null,
      ebitda:    ebitdaCY,
      ebitdaLY:  ebitdaLY,
    };
  });

  // Liquidity test
  const cash        = qRecord?.cash_balance;
  const liabilities = qRecord?.current_liabilities;
  const proposed    = qRecord?.proposed_distribution;
  const cashPost    = cash != null && proposed != null ? cash - proposed : null;
  const solvencyOk  = cashPost != null ? cashPost >= 200000 : null;

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Confidentiality header */}
      <div style={{ background: C.charcoal, padding: '14px 20px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>🔒</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: C.white, marginBottom: 2 }}>
              Strictly Confidential — Shareholders Only
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
              This report is intended solely for the named shareholder. Do not forward or distribute.
              All figures are unaudited management accounts, subject to year-end adjustment. A dividend
              distribution may only be made following a formal auditor distribution test per Art. 9.1 of
              the Shareholders' Agreement.
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 16px 48px' }}>

        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Shareholder Report
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.charcoal }}>
            {bvName} — {q.label} {year}
          </h1>
          <div style={{ fontSize: 13, color: C.mid, marginTop: 2 }}>
            Trading as {tradingName}
          </div>
          <div style={{ fontSize: 13, color: C.mid, marginTop: 4 }}>
            {q.months.map(m => MONTHS[m - 1]).join(' · ')}
          </div>
        </div>

        {/* KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Revenue', value: fmt(cy.revenue),            color: C.charcoal },
            { label: 'EBITDA',  value: fmt(Math.round(cy.ebitda)), color: cy.ebitda >= 0 ? C.green : C.red },
            { label: 'Covers',  value: cy.covers ? cy.covers : '—', color: C.charcoal },
          ].map(k => (
            <div key={k.label} style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* P&L */}
        <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.charcoal, marginBottom: 14 }}>Profit & Loss — {q.label} {year}</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:C.cream, borderBottom:`1px solid ${C.border}` }}>
                {['', 'Actual', String(year-1), 'vs LY %'].map((h,i) => (
                  <th key={i} style={{ padding:isMobile?'6px 4px':('8px 0 8px '+(i===0?'0':'16px')), textAlign:i===0?'left':'right', fontSize:10, fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <PLRow label='Revenue'         cy={cy.revenue}       ly={ly.revenue}       bold isRevenue isMobile={isMobile}/>
              <PLRow label='Food Cost'       cy={cy.food_cost}     ly={ly.food_cost}     indent isMobile={isMobile}/>
              {!isSassys && <PLRow label='Bev Cost'      cy={cy.bev_cost}    ly={ly.bev_cost}    indent dimIfZero isMobile={isMobile}/>}
              {isSassys  && <PLRow label='Oedipus Costs' cy={cy.oedipus}     ly={ly.oedipus}     indent dimIfZero isMobile={isMobile}/>}
              <PLRow label='Payroll'         cy={cy.payroll}       ly={ly.payroll}       indent isMobile={isMobile}/>
              {cy.lm_total > 0 && <PLRow label='LM Allocations' cy={cy.lm_total} ly={ly.lm_total>0?ly.lm_total:null} indent dimIfZero isMobile={isMobile}/>}
              <PLRow label='Management Fees' cy={cy.group_costs}   ly={ly.group_costs}   indent dimIfZero isMobile={isMobile}/>
              <PLRow label='Other OpEx'      cy={cy.other_opex}    ly={ly.other_opex}    indent dimIfZero isMobile={isMobile}/>
              <PLRow label='Total Expenses'  cy={cy.totalExpense}  ly={ly.totalExpense}  bold isMobile={isMobile}/>
              <PLRow label='EBITDA'          cy={cy.ebitda}        ly={ly.ebitda}        bold highlight isRevenue isMobile={isMobile}/>
              {cy.net_profit !== 0 && <PLRow label='Net Profit' cy={cy.net_profit} ly={ly.net_profit} bold isRevenue isMobile={isMobile}/>}
            </tbody>
          </table>
        </div>

        {/* Revenue chart — CY vs LY */}
        {chartData.some(d => d.revenue || d.revenueLY) && (
          <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.charcoal, marginBottom: 2 }}>Revenue — {q.label} {year} vs {year - 1}</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 14 }}>{q.months.map(m => MONTHS[m - 1]).join(' · ')}</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} barGap={4} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#d1d9e0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#4a5568', fontWeight: 600 }} axisLine={{ stroke: '#94a3b8' }} tickLine={{ stroke: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#4a5568' }} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} width={44} axisLine={{ stroke: '#94a3b8' }} tickLine={{ stroke: '#94a3b8' }} />
                <Tooltip formatter={(v, name) => [v ? `€${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—', name]} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Bar dataKey="revenue"   name={String(year)}     fill={COL_CY} radius={[3, 3, 0, 0]} />
                <Bar dataKey="revenueLY" name={String(year - 1)} fill={COL_LY} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* EBITDA chart — CY vs LY */}
        {chartData.some(d => d.ebitda !== null || d.ebitdaLY !== null) && (
          <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.charcoal, marginBottom: 2 }}>EBITDA — {q.label} {year} vs {year - 1}</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 14 }}>{q.months.map(m => MONTHS[m - 1]).join(' · ')}</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} barGap={4} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#d1d9e0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#4a5568', fontWeight: 600 }} axisLine={{ stroke: '#94a3b8' }} tickLine={{ stroke: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#4a5568' }} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} width={44} axisLine={{ stroke: '#94a3b8' }} tickLine={{ stroke: '#94a3b8' }} />
                <Tooltip formatter={(v, name) => [v != null ? `€${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—', name]} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <ReferenceLine y={0} stroke={C.charcoal} strokeWidth={2} />
                <Bar dataKey="ebitda"   name={String(year)}     fill={COL_CY} radius={[3, 3, 0, 0]} />
                <Bar dataKey="ebitdaLY" name={String(year - 1)} fill={COL_LY} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Board narrative */}
        {qRecord?.narrative && (
          <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.charcoal, marginBottom: 10 }}>Board Commentary</div>
            <div style={{ fontSize: 13, color: C.charcoal, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{qRecord.narrative}</div>
          </div>
        )}

        {/* Governance log */}
        {logEntries.length > 0 && (
          <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.charcoal, marginBottom: 12 }}>Governance Log</div>
            {logEntries.map(entry => (
              <div key={entry.id} style={{ background: C.cream, borderRadius: 8, padding: '10px 14px', marginBottom: 8, borderLeft: `3px solid ${C.navy}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.navy }}>{LOG_CATEGORY_LABELS[entry.category] || entry.category}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontSize: 11, color: C.mid }}>{MONTHS[entry.month - 1]}</span>
                    {entry.amount && <span style={{ fontSize: 12, fontWeight: 700, color: C.charcoal }}>{fmt(entry.amount)}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.charcoal, marginBottom: entry.description ? 2 : 0 }}>{entry.title}</div>
                {entry.description && <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5 }}>{entry.description}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Liquidity / dividend test */}
        {(cash != null || proposed != null) && (
          <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.charcoal, marginBottom: 4 }}>Dividend / Liquidity Position</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 14 }}>Per Art. 9 — distribution subject to formal auditor test</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {cash != null && (
                <div style={{ background: C.cream, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 3 }}>Cash balance</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.charcoal }}>{fmt(cash)}</div>
                </div>
              )}
              {liabilities != null && (
                <div style={{ background: C.cream, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 3 }}>Current liabilities</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.charcoal }}>{fmt(liabilities)}</div>
                </div>
              )}
              {proposed != null && (
                <div style={{ background: C.cream, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 3 }}>Proposed distribution</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.amber }}>{fmt(proposed)}</div>
                </div>
              )}
              {cashPost != null && (
                <div style={{ background: solvencyOk ? C.greenLight : C.redLight, borderRadius: 8, padding: '12px 14px', border: `1px solid ${solvencyOk ? C.green : C.red}` }}>
                  <div style={{ fontSize: 11, color: C.mid, marginBottom: 3 }}>Cash post-distribution</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: solvencyOk ? C.green : C.red }}>{fmt(cashPost)}</div>
                  <div style={{ fontSize: 10, color: solvencyOk ? C.green : C.red, fontWeight: 700, marginTop: 3 }}>
                    {solvencyOk ? '✓ Meets €200k floor' : '✗ Below €200k floor'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Art. 9 */}
        <div style={{ background: C.cream, borderRadius: 10, padding: '14px 18px', border: `1px solid ${C.border}`, marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Article 9 — Dividend Policy</div>
          <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.6 }}>
            The Company shall distribute at least 80% of net profit annually, provided solvency remains ≥ 30%
            post-distribution and cash reserves exceed €200,000. A 20% gross profit reserve for future investments
            applies before any distribution. Distributions require resolution of the General Meeting of Shareholders
            and a formal auditor distribution test.
          </div>
        </div>

        {/* Read confirmation */}
        <div style={{ background: readConfirmed ? C.greenLight : C.white, border: `1.5px solid ${readConfirmed ? C.green : C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 8, textAlign: 'center' }}>
          {readConfirmed ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.green, marginBottom: 4 }}>✓ Report confirmed as read</div>
              <div style={{ fontSize: 12, color: C.mid }}>Your acknowledgement has been recorded.</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.charcoal, marginBottom: 12 }}>
                Please confirm you have read and reviewed this report.
              </div>
              <button
                onClick={handleConfirmRead}
                style={{ padding: '10px 28px', background: C.navy, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
              >
                I confirm I have read this report
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 11, color: C.mid, lineHeight: 1.6 }}>
          {bvName} · Trading as {tradingName} · Confidential · Unaudited management accounts
        </div>

      </div>
    </div>
  );
}